<script setup lang="ts">
import type { DialogContentEmits, PointerDownOutsideEvent } from 'reka-ui'

import { cn } from 'cn'
import { capitalize } from '@docue/utils/string'
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits,
} from 'reka-ui'

import type { DialogContentProps } from './types'

import { dialogContentVariants } from './variants'

const props = withDefaults(defineProps<DialogContentProps>(), {
  align: 'center',
})

const emits = defineEmits<DialogContentEmits>()

const { t: tg } = useI18n({ useScope: 'global' })

const delegatedProps = computed(() => {
  const {
    class: _,
    omitCloseButton: _1,
    closeDisabled: _2,
    align: _3,
    dismissOutsideBoundsOnly: _4,
    ...delegated
  } = props
  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)

const refContent = useTemplateRef('content')

const onPointerDownOutside = (e: PointerDownOutsideEvent) => {
  const content = refContent.value?.$el as HTMLElement | undefined
  if (!props.dismissOutsideBoundsOnly || !content)
    return

  const { clientX, clientY } = e.detail.originalEvent
  const { top, right, bottom, left } = content.getBoundingClientRect()

  const isInsideContent = clientX >= left && clientX <= right && clientY >= top && clientY <= bottom
  if (isInsideContent)
    e.preventDefault()
}
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      :class="cn(
        'fixed',
        'inset-0',
        'z-50',
        'bg-black/80',
        'data-[state=open]:animate-in',
        'data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0',
        'data-[state=open]:fade-in-0',
      )"
    />
    <DialogContent
      ref="content"
      v-bind="forwarded"
      :class="cn(dialogContentVariants({ align }), props.class)"
      @pointer-down-outside="onPointerDownOutside"
    >
      <slot />
      <DialogClose
        v-if="!omitCloseButton"
        class="absolute left-4 top-[18px] z-10"
        as-child
      >
        <UiButton
          :aria-label="capitalize(tg('close'))"
          :disabled="closeDisabled"
          variant="ghost"
          size="sm"
          icon="docue:x-close"
          :class="cn(
            'opacity-70',
            'transition-colors',
            'hover:opacity-100',
            'disabled:pointer-events-none',
            'data-[state=open]:bg-neutral-100',
            'data-[state=open]:text-neutral-500',
            'dark:data-[state=open]:bg-neutral-800',
            'dark:data-[state=open]:text-neutral-400',
          )"
        />
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
